/*
 * QuickPaste's Copy Current Frame, hands on (sections/quickpaste-frame.liquid).
 *
 * A Program monitor plays a three-clip edit (the theme's mako clips, 8s
 * each). Drag the ruler to scrub, ←/→ step a frame, Space plays/pauses.
 * ⌘⇧C / Ctrl+Shift+C (or the Copy Current Frame button) copies the frame
 * under the playhead: the still is drawn from the video into a canvas, with
 * a shutter flash and a "Frame copied" toast. ⌘V / Ctrl+V (or Paste) drops
 * it into the chat. With Add Frame to Project Bin on, it also lands in the
 * Project bin as "Sequence 01 Frame.png".
 *
 * Real keys only count while the pointer is over the demo or focus is in
 * it, and ⌘V is only taken over when a frame has been copied - the page's
 * own copy and paste are never blocked, and the real clipboard is never read.
 *
 * A short tour plays as soon as the demo is on screen, then hands over
 * ("Your turn"). Any touch hands over at once. Off screen it stops (and
 * starts again from the top next time); with reduced motion it hands over
 * at once, paused, with one frame already pasted.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const noHover = window.matchMedia('(hover: none)');
  const STOP = {};
  const FPS = 25;
  const CLIP = 8;
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  // Running text: "press ⌘⇧C" reads "press Ctrl+Shift+C" on Windows.
  const forOs = (text) =>
    isMac ? text : text.replace(/([⌃⌥⇧⌘]+)(\S)/g, (m, mods, key) => [...mods].map((c) => WIN[c]).concat(key).join('+'));

  const timecode = (t) => {
    const f = Math.max(0, Math.round(t * FPS));
    const p = (n) => String(n).padStart(2, '0');
    return `${p(Math.floor(f / (FPS * 3600)))}:${p(Math.floor(f / (FPS * 60)) % 60)}:${p(Math.floor(f / FPS) % 60)}:${p(f % FPS)}`;
  };

  const init = (stage) => {
    if (stage.dataset.qfReady) return;
    stage.dataset.qfReady = 'true';

    const $ = (s) => stage.querySelector(s);
    const text = {};
    const tpl = $('[data-qf-copy-text]');
    if (tpl) tpl.content.querySelectorAll('[data-k]').forEach((el) => (text[el.dataset.k] = el.textContent.trim()));

    const videos = [...stage.querySelectorAll('[data-qf-video]')];
    const TOTAL = CLIP * videos.length;
    const posters = videos.map(() => null);
    const playhead = $('[data-qf-playhead]');
    const tcEl = $('[data-qf-tc]');
    const playBtn = $('[data-qf-play]');
    const ruler = $('[data-qf-ruler]');
    const rulerIn = ruler.querySelector('.qf-ruler-in');
    const screen = $('[data-qf-screen]');
    const flash = $('[data-qf-flash]');
    const shutter = $('[data-qf-shutter]');
    const toast = $('[data-qf-toast]');
    const toastThumb = $('[data-qf-toast-thumb]');
    const toastTc = $('[data-qf-toast-tc]');
    const copyBtn = $('[data-qf-copy]');
    const sw = $('[data-qf-switch]');
    const binRow = $('[data-qf-binrow]');
    const bin = $('[data-qf-bin]');
    const thread = $('[data-qf-thread]');
    const composer = $('[data-qf-composer]');
    const pasteBtn = $('[data-qf-paste]');
    const capTitle = $('[data-qf-cap-title]');
    const capText = $('[data-qf-cap-text]');
    const replayBtn = $('[data-qf-replay]');
    const replayText = $('[data-qf-replay-text]');
    const frameName = stage.dataset.frameName || 'Sequence 01 Frame';
    const keys = {};
    stage.querySelectorAll('[data-qf-key]').forEach((k) => (keys[k.dataset.qfKey] = k));
    const combos = {};
    stage.querySelectorAll('[data-qf-combo]').forEach((c) => (combos[c.dataset.qfCombo] = c));

    // Windows: Ctrl and Shift, spelled out
    if (!isMac) {
      stage.querySelectorAll('[data-win]').forEach((k) => {
        k.textContent = k.dataset.win;
        k.classList.add('qf-key--word');
      });
      stage.querySelectorAll('[data-qf-os]').forEach((k) => (k.textContent = 'Ctrl+Shift+C'));
    }

    let clip = 0;
    let t = 0;
    let wantPlay = false;
    let visible = false;
    let raf = 0;
    let copied = null;
    let frames = 0;
    let replied = false;
    let touring = false;
    let toured = false;
    let run = 0;
    let hovered = false;
    let toastTimer = 0;
    let capTimer = 0;

    /* ---------- Playback ---------- */
    const video = () => videos[clip];

    const render = () => {
      playhead.style.setProperty('--qf-p', (t / TOTAL).toFixed(5));
      tcEl.textContent = timecode(t);
      const playing = wantPlay && visible && !document.hidden;
      playBtn.classList.toggle('is-playing', playing);
      playBtn.setAttribute('aria-label', playing ? text.pause || 'Pause' : text.play || 'Play');
    };

    const showClip = (i) => {
      if (i === clip) return;
      videos[clip].pause();
      videos[clip].classList.remove('is-on');
      clip = i;
      videos[clip].classList.add('is-on');
    };

    const seek = (time) => {
      t = Math.min(Math.max(time, 0), TOTAL - 1 / FPS);
      const i = Math.min(videos.length - 1, Math.floor(t / CLIP));
      showClip(i);
      try {
        video().currentTime = t - i * CLIP;
      } catch (e) {
        /* not seekable yet */
      }
      applyPlayback();
      render();
    };

    const applyPlayback = () => {
      const active = wantPlay && visible && !document.hidden;
      videos.forEach((v, i) => {
        if (i === clip && active) {
          if (v.paused) v.play().catch(() => {});
        } else if (!v.paused) v.pause();
      });
      if (active && !raf) raf = requestAnimationFrame(tick);
      render();
    };

    const tick = () => {
      raf = 0;
      if (!(wantPlay && visible && !document.hidden)) return;
      const v = video();
      const local = v.currentTime || 0;
      if (v.ended || local >= CLIP - 0.04) {
        seek(((clip + 1) % videos.length) * CLIP);
      } else {
        t = clip * CLIP + local;
        render();
      }
      raf = requestAnimationFrame(tick);
    };

    const setPlay = (on) => {
      wantPlay = on;
      if (!on) {
        const v = video();
        if (v.readyState >= 1) t = clip * CLIP + v.currentTime;
      }
      applyPlayback();
    };

    videos.forEach((v, i) => {
      v.addEventListener('ended', () => {
        if (i === clip && wantPlay) seek(((clip + 1) % videos.length) * CLIP);
      });
    });

    const loadPosters = () => {
      videos.forEach((v, i) => {
        if (posters[i] || !v.poster) return;
        const img = new Image();
        img.src = v.poster;
        posters[i] = img;
      });
    };

    /* ---------- Keycaps ---------- */
    const KEYS = { copy: ['mod', 'shift', 'c'], paste: ['mod2', 'v'] };
    const lightCombo = (name, on) => {
      KEYS[name].forEach((k) => keys[k] && keys[k].classList.toggle('is-down', on));
      combos[name].classList.toggle('is-lit', on);
    };
    const flashCombo = (name) => {
      lightCombo(name, true);
      window.setTimeout(() => lightCombo(name, false), 260);
    };

    /* ---------- Copy ---------- */
    const grab = () => {
      const v = video();
      let src = null;
      let w = 960;
      let h = 540;
      if (v.readyState >= 2 && v.videoWidth) {
        src = v;
        w = v.videoWidth;
        h = v.videoHeight;
      } else {
        loadPosters();
        const p = posters[clip];
        if (p && p.complete && p.naturalWidth) {
          src = p;
          w = p.naturalWidth;
          h = p.naturalHeight;
        }
      }
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (src) ctx.drawImage(src, 0, 0, w, h);
      else {
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, w, h);
      }
      return c;
    };

    const drawInto = (canvas, from) => {
      const ctx = canvas.getContext('2d');
      ctx.drawImage(from, 0, 0, canvas.width, canvas.height);
      return canvas;
    };

    const copy = () => {
      if (wantPlay) {
        const v = video();
        if (v.readyState >= 1) t = clip * CLIP + v.currentTime;
      }
      const tc = timecode(t);
      copied = { canvas: grab(), tc };
      render();

      if (!still.matches) {
        flash.animate([{ opacity: 0 }, { opacity: 0.85, offset: 0.14 }, { opacity: 0 }], { duration: 520, easing: 'ease-out' });
        shutter.animate(
          [
            { opacity: 1, boxShadow: 'inset 0 0 0 0 rgb(0 0 0 / 0.85)' },
            { opacity: 1, boxShadow: 'inset 0 0 0 24px rgb(0 0 0 / 0.85)', offset: 0.22 },
            { opacity: 0, boxShadow: 'inset 0 0 0 0 rgb(0 0 0 / 0.85)' },
          ],
          { duration: 520, easing: 'cubic-bezier(0.3, 0, 0.2, 1)' }
        );
      }
      copyBtn.classList.add('is-down');
      window.setTimeout(() => copyBtn.classList.remove('is-down'), 220);

      drawInto(toastThumb, copied.canvas);
      toastTc.textContent = tc;
      toast.classList.add('is-on');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => toast.classList.remove('is-on'), 2400);

      if (sw.getAttribute('aria-checked') === 'true') {
        frames += 1;
        const li = document.createElement('li');
        li.className = 'qf-item qf-item--new';
        li.dataset.qfAdded = '';
        const ico = document.createElement('canvas');
        ico.className = 'qf-ico';
        ico.width = 44;
        ico.height = 26;
        drawInto(ico, copied.canvas);
        const name = document.createElement('span');
        name.textContent = `${frames > 1 ? `${frameName} ${frames}` : frameName}.png`;
        li.append(ico, name);
        bin.append(li);
        bin.scrollTop = bin.scrollHeight;
      }
      composer.classList.add('is-ready');
    };

    /* ---------- Paste ---------- */
    const trimThread = () => {
      const msgs = thread.querySelectorAll('.qf-msg');
      for (let i = 0; i < msgs.length - 7; i += 1) msgs[i].remove();
      thread.scrollTop = thread.scrollHeight;
    };

    const addPhoto = (source, tc) => {
      const msg = document.createElement('div');
      msg.className = 'qf-msg qf-msg--me qf-msg--photo';
      msg.dataset.qfAdded = '';
      const fig = document.createElement('span');
      fig.className = 'qf-photo';
      const c = document.createElement('canvas');
      c.width = 480;
      c.height = 270;
      c.setAttribute('role', 'img');
      c.setAttribute('aria-label', `${frameName}.png, ${tc}`);
      drawInto(c, source);
      const chip = document.createElement('span');
      chip.className = 'qf-photo-tc';
      chip.textContent = tc;
      fig.append(c, chip);
      msg.append(fig);
      thread.append(msg);
      trimThread();
    };

    const addReply = () => {
      if (replied || !stage.dataset.reply) return;
      replied = true;
      const msg = document.createElement('div');
      msg.className = 'qf-msg qf-msg--them';
      msg.dataset.qfAdded = '';
      const bubble = document.createElement('span');
      bubble.className = 'qf-bubble';
      bubble.textContent = stage.dataset.reply;
      msg.append(bubble);
      thread.append(msg);
      trimThread();
    };

    const paste = () => {
      pasteBtn.classList.add('is-down');
      window.setTimeout(() => pasteBtn.classList.remove('is-down'), 160);
      if (!copied) {
        caption(text.hint_title, text.hint_first, 2200);
        return false;
      }
      addPhoto(copied.canvas, copied.tc);
      composer.classList.remove('is-ready');
      if (!replied) {
        const r = run;
        window.setTimeout(() => {
          if (r === run) addReply();
        }, 900);
      }
      return true;
    };

    /* ---------- Caption ---------- */
    const hint = () => forOs((noHover.matches ? text.hint_touch : text.hint_text) || '');
    const setCap = (title, body) => {
      capTitle.textContent = title || '';
      capText.textContent = forOs(body || '');
    };
    const restCaption = () => (touring ? null : setCap(text.hint_title, hint()));
    const caption = (title, body, ms) => {
      setCap(title, body);
      window.clearTimeout(capTimer);
      if (ms) capTimer = window.setTimeout(restCaption, ms);
    };

    /* ---------- Tour ---------- */
    const wait = (ms, r) =>
      new Promise((resolve, reject) =>
        window.setTimeout(() => (r === run && touring ? resolve() : reject(STOP)), ms)
      );

    const pressCombo = async (name, r, action) => {
      const ks = KEYS[name];
      combos[name].classList.add('is-lit');
      for (const k of ks) {
        keys[k].classList.add('is-down');
        await wait(110, r);
      }
      action();
      await wait(240, r);
      lightCombo(name, false);
    };

    const reset = () => {
      thread.querySelectorAll('[data-qf-added]').forEach((n) => n.remove());
      bin.querySelectorAll('[data-qf-added]').forEach((n) => n.remove());
      sw.setAttribute('aria-checked', 'false');
      binRow.classList.remove('is-hint');
      composer.classList.remove('is-ready');
      toast.classList.remove('is-on');
      lightCombo('copy', false);
      lightCombo('paste', false);
      copied = null;
      frames = 0;
      replied = false;
    };

    const runTour = async () => {
      run += 1;
      const r = run;
      touring = true;
      reset();
      replayText.textContent = text.skip || 'Try it';
      setCap(text.tour_title, text.step_play);
      wantPlay = true;
      seek(1.2);
      try {
        await wait(1300, r);
        setCap(text.tour_title, text.step_copy);
        await wait(250, r);
        await pressCombo('copy', r, copy);
        await wait(1000, r);
        setCap(text.tour_title, text.step_paste);
        await wait(250, r);
        await pressCombo('paste', r, paste);
        await wait(1500, r);
        setCap(text.tour_title, text.step_scrub);
        setPlay(false);
        const from = t;
        const to = 17.6;
        for (let i = 1; i <= 8; i += 1) {
          await wait(80, r);
          seek(from + ((to - from) * i) / 8);
        }
        await wait(450, r);
        setCap(text.tour_title, text.step_bin);
        binRow.classList.add('is-hint');
        await wait(600, r);
        sw.setAttribute('aria-checked', 'true');
        await wait(600, r);
        binRow.classList.remove('is-hint');
        await pressCombo('copy', r, copy);
        await wait(1000, r);
        await pressCombo('paste', r, paste);
        await wait(900, r);
        endTour();
        setPlay(true);
      } catch (e) {
        if (e !== STOP) throw e;
      }
    };

    // Hand over: keep what the tour did, so the visitor picks up from there.
    const endTour = () => {
      if (!touring) return;
      run += 1;
      touring = false;
      toured = true;
      binRow.classList.remove('is-hint');
      lightCombo('copy', false);
      lightCombo('paste', false);
      replayText.textContent = text.replay || 'Replay';
      if (copied && !replied) window.setTimeout(addReply, 700);
      setCap(text.hint_title, hint());
    };

    // Leaving mid-tour stops it; it plays from the top next time.
    const abandonTour = () => {
      run += 1;
      touring = false;
      reset();
    };

    // Any visitor input hands over, mid-tour or before one ever ran.
    const handOver = () => {
      if (touring) endTour();
      else if (!toured) {
        run += 1;
        toured = true;
        replayText.textContent = text.replay || 'Replay';
        setCap(text.hint_title, hint());
      }
    };

    replayBtn.addEventListener('click', () => {
      if (touring) {
        endTour();
        stage.focus({ preventScroll: true });
      } else runTour();
    });

    /* ---------- Visitor input ---------- */
    stage.addEventListener(
      'pointerdown',
      (e) => {
        if (!replayBtn.contains(e.target)) handOver();
      },
      true
    );
    stage.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') hovered = true;
    });
    stage.addEventListener('pointerleave', () => {
      hovered = false;
      lightCombo('copy', false);
      lightCombo('paste', false);
    });

    playBtn.addEventListener('click', () => setPlay(!(wantPlay && visible)));
    screen.addEventListener('click', () => setPlay(!wantPlay));
    copyBtn.addEventListener('click', () => {
      flashCombo('copy');
      copy();
    });
    pasteBtn.addEventListener('click', () => {
      flashCombo('paste');
      paste();
    });
    sw.addEventListener('click', () => {
      sw.setAttribute('aria-checked', sw.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
    });

    // Scrub: drag along the ruler
    let scrubbing = false;
    let rect = null;
    const scrubTo = (x) => seek(((x - rect.left) / rect.width) * TOTAL);
    ruler.addEventListener('pointerdown', (e) => {
      if (e.button) return;
      scrubbing = true;
      rect = rulerIn.getBoundingClientRect();
      ruler.setPointerCapture(e.pointerId);
      setPlay(false);
      scrubTo(e.clientX);
    });
    ruler.addEventListener('pointermove', (e) => {
      if (scrubbing) scrubTo(e.clientX);
    });
    const endScrub = () => (scrubbing = false);
    ruler.addEventListener('pointerup', endScrub);
    ruler.addEventListener('pointercancel', endScrub);

    // Real keys, only while the pointer is over the demo or focus is in it.
    const armed = () => hovered || stage.contains(document.activeElement);
    const modOf = (e) => (isMac ? e.metaKey : e.ctrlKey) || e.metaKey || e.ctrlKey;
    const showHeld = (e) => {
      const mod = modOf(e);
      if (keys.mod) keys.mod.classList.toggle('is-down', mod && e.shiftKey);
      if (keys.shift) keys.shift.classList.toggle('is-down', mod && e.shiftKey);
      if (keys.mod2) keys.mod2.classList.toggle('is-down', mod && !e.shiftKey);
    };
    document.addEventListener('keydown', (e) => {
      if (!armed() || e.altKey) return;
      const typing = e.target.closest && e.target.closest('input, textarea, [contenteditable]');
      if (typing) return;
      if (!touring) showHeld(e);
      const mod = modOf(e);
      const k = e.code || '';
      if (mod && e.shiftKey && (k === 'KeyC' || /^c$/i.test(e.key))) {
        e.preventDefault();
        handOver();
        flashCombo('copy');
        copy();
      } else if (mod && !e.shiftKey && (k === 'KeyV' || /^v$/i.test(e.key))) {
        handOver();
        flashCombo('paste');
        if (copied) {
          e.preventDefault();
          paste();
        } else caption(text.hint_title, text.hint_first, 2200);
      } else if (e.target === stage && !mod) {
        if (e.key === ' ') {
          e.preventDefault();
          handOver();
          setPlay(!wantPlay);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          handOver();
          setPlay(false);
          seek(t + (e.key === 'ArrowLeft' ? -1 : 1) / FPS);
        }
      }
    });
    document.addEventListener('keyup', (e) => {
      if (!touring && armed()) showHeld(e);
    });
    window.addEventListener('blur', () => {
      if (!touring) {
        lightCombo('copy', false);
        lightCombo('paste', false);
      }
    });

    /* ---------- On screen ---------- */
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      loadPosters();
      videos.forEach((v) => (v.preload = 'auto'));
    };

    // Reduced motion: hand over at once, paused, one still already pasted.
    const settleStill = () => {
      toured = true;
      replayText.textContent = text.replay || 'Replay';
      setCap(text.hint_title, hint());
      seek(0);
      loadPosters();
      const p = posters[0];
      const place = () => {
        if (thread.querySelector('[data-qf-added]') || !p.naturalWidth) return;
        copied = { canvas: grab(), tc: timecode(0) };
        addPhoto(copied.canvas, copied.tc);
        replied = true;
      };
      if (p) {
        if (p.complete) place();
        else p.addEventListener('load', place, { once: true });
      }
    };

    const wake = () => {
      warm();
      if (!toured && !touring) {
        if (still.matches) settleStill();
        else {
          runTour();
          return;
        }
      }
      applyPlayback();
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && warm(), {
        rootMargin: '600px 0px',
      }).observe(stage);

      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) wake();
            else {
              if (touring) {
                abandonTour();
                wantPlay = false;
              }
              applyPlayback();
            }
          });
        },
        { threshold: 0.2 }
      ).observe(stage);
    } else {
      visible = true;
      warm();
      settleStill();
    }

    document.addEventListener('visibilitychange', applyPlayback);

    setCap(text.tour_title, text.step_play);
    render();
  };

  const boot = () => document.querySelectorAll('[data-qf-demo]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
